import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import apiClient from "../../../utils/apiClient";
import { AUTO } from "../processCarrierWorkbook";
import { buildCarrierSummary } from "../summary";
import { formatCarrierLabel } from "../presentation";
import { formatSaveProgress } from "../saveProgress";

const UPLOAD_ENDPOINT = "/api/v1/difotTools/carrier-integration/upload";

const PREVIEW_ROW_LIMIT = 150;

const ACCEPTED_FILE_TYPES = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
};

const MAX_FILE_SIZE = 50 * 1024 * 1024;

const PREVIEW_CONCURRENCY = 2;
const SAVE_CONCURRENCY = 1;

async function runWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function consume() {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await worker(items[currentIndex], currentIndex);
    }
  }

  const workerCount = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => consume()));

  return results;
}

function mapApiRecordToUi(record) {
  return {
    sourceCarrier: record.source_carrier,
    sourceSheet: record.source_sheet,
    consignmentId: record.consignment_id,
    kpiHit: record.carrier_kpi_hit,
    carrierDespatchDate: record.carrier_despatch_date,
    carrierEta: record.carrier_eta,
    carrierCompletedDate: record.carrier_completed_date,
    reconciledKpiExplanation: record.carrier_kpi_explanation || "",
    ruleApplied: record.rule_applied || "",
    rawStatus: record.raw_status || "",
    forceMajeureReason: record.force_majeure_reason || "",
  };
}

function mapApiPayload(payload) {
  return {
    fileName: payload.file_name,
    carrier: formatCarrierLabel(payload.carrier_type),
    detectedCarrier: formatCarrierLabel(payload.detected_carrier_type),
    saveSuccess:
      payload.save_success === null || payload.save_success === undefined
        ? null
        : Boolean(payload.save_success),
    saveMessage: payload.save_message || "",
    saveResult: payload.save_result || null,
    reportingPeriod: payload.reporting_period || "",
    carrierReport: payload.carrier_report || null,
    sheetSummaries: Array.isArray(payload.sheet_summaries)
      ? payload.sheet_summaries.map((sheet) => ({
          name: sheet.name,
          rowCount: sheet.row_count,
        }))
      : [],
    records: Array.isArray(payload.records)
      ? payload.records.map(mapApiRecordToUi)
      : [],
    savePayload: payload.save_payload || null,
  };
}

function difotColorsFor(summary) {
  if (summary.total === 0) {
    return {
      text: "text-slate-400",
      bg: "bg-slate-50",
      border: "border-slate-200",
      bar: "bg-slate-300",
    };
  }

  const rate = summary.hit / summary.total;
  if (rate >= 0.9) {
    return {
      text: "text-emerald-600",
      bg: "bg-gradient-to-br from-emerald-50 to-emerald-100/50",
      border: "border-emerald-200",
      bar: "bg-emerald-500",
    };
  }
  if (rate >= 0.75) {
    return {
      text: "text-amber-600",
      bg: "bg-gradient-to-br from-amber-50 to-amber-100/50",
      border: "border-amber-200",
      bar: "bg-amber-500",
    };
  }
  return {
    text: "text-red-600",
    bg: "bg-gradient-to-br from-red-50 to-red-100/50",
    border: "border-red-200",
    bar: "bg-red-500",
  };
}

export default function useCarrierDifotUpload() {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [processedFiles, setProcessedFiles] = useState([]);
  const [processedRecords, setProcessedRecords] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const processFiles = useCallback(async (filesToProcess, saveToDb = false) => {
    if (filesToProcess.length === 0) {
      setProcessedFiles([]);
      setProcessedRecords([]);
      return;
    }

    if (saveToDb) setIsSaving(true);
    else setIsProcessing(true);

    try {
      const settled = await runWithConcurrency(
        filesToProcess,
        saveToDb ? SAVE_CONCURRENCY : PREVIEW_CONCURRENCY,
        async (item) => {
          try {
            const formData = new FormData();
            formData.append("file", item.file);
            formData.append("carrier_type", item.selectedCarrier);
            formData.append("save_to_db", saveToDb ? "true" : "false");

            const response = await apiClient.post(UPLOAD_ENDPOINT, formData);
            const payload = await response.json();
            if (!response.ok) {
              throw new Error(
                payload?.detail || payload?.message || "Failed to process file."
              );
            }

            return { status: "success", result: mapApiPayload(payload) };
          } catch (error) {
            return {
              status: "error",
              fileName: item.file.name,
              error: error.message || "Failed to process file.",
            };
          }
        }
      );

      const successful = settled
        .filter((item) => item.status === "success")
        .map((item) => item.result);
      const failed = settled.filter((item) => item.status === "error");

      setProcessedFiles([
        ...successful,
        ...failed.map((item) => ({
          fileName: item.fileName,
          carrier: "Unknown",
          detectedCarrier: "Unknown",
          sheetSummaries: [],
          error: item.error,
        })),
      ]);
      setProcessedRecords(successful.flatMap((item) => item.records));

      if (successful.length > 0) {
        toast.success(
          saveToDb
            ? `${successful.length} carrier file(s) processed and sent to Snowflake.`
            : `${successful.length} carrier file(s) processed for preview.`
        );
      }
      successful
        .filter((item) => item.saveSuccess === false)
        .forEach((item) => {
          const progressLabel = formatSaveProgress(item);
          toast.warning(
            `${item.fileName}: ${progressLabel ? `${progressLabel}. ` : ""}${
              item.saveMessage || "parsed successfully, but Snowflake save failed."
            }`
          );
        });
      failed.forEach((item) => {
        toast.error(`${item.fileName}: ${item.error}`);
      });
    } finally {
      setIsProcessing(false);
      setIsSaving(false);
    }
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const nextFiles = acceptedFiles.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        file,
        selectedCarrier: AUTO,
      }));
      const nextState = [...uploadedFiles, ...nextFiles];
      setUploadedFiles(nextState);
      setProcessedFiles([]);
      setProcessedRecords([]);
      toast.success(`${nextFiles.length} file(s) added.`);
      await processFiles(nextState, false);
    },
    [processFiles, uploadedFiles]
  );

  const dropzone = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
  });

  const updateFileCarrier = useCallback(
    (id, selectedCarrier) => {
      setUploadedFiles((prev) => {
        const nextFiles = prev.map((item) =>
          item.id === id ? { ...item, selectedCarrier } : item
        );
        setProcessedFiles([]);
        setProcessedRecords([]);
        processFiles(nextFiles, false);
        return nextFiles;
      });
    },
    [processFiles]
  );

  const removeFile = useCallback((id) => {
    setUploadedFiles((prev) => prev.filter((item) => item.id !== id));
    setProcessedFiles([]);
    setProcessedRecords([]);
  }, []);

  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
    setProcessedFiles([]);
    setProcessedRecords([]);
  }, []);

  const saveToSnowflake = useCallback(async () => {
    if (uploadedFiles.length === 0) {
      toast.warning("Upload at least one carrier file first.");
      return;
    }
    await processFiles(uploadedFiles, true);
  }, [processFiles, uploadedFiles]);

  const summary = useMemo(
    () => buildCarrierSummary(processedRecords),
    [processedRecords]
  );

  const difotPct = useMemo(() => {
    if (summary.total === 0) return "—";
    return `${((summary.hit / summary.total) * 100).toFixed(1)}%`;
  }, [summary]);

  const difotColors = useMemo(() => difotColorsFor(summary), [summary]);

  const previewRows = useMemo(
    () => processedRecords.slice(0, PREVIEW_ROW_LIMIT),
    [processedRecords]
  );

  return {
    uploadedFiles,
    processedFiles,
    processedRecords,
    isProcessing,
    isSaving,
    dropzone,
    updateFileCarrier,
    removeFile,
    clearFiles,
    saveToSnowflake,
    summary,
    difotPct,
    difotColors,
    previewRows,
  };
}
